import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getSupabase, isSupabaseConfigured, loadSupabaseConfigFromApi, setRuntimeSupabaseConfig } from '../lib/supabase';
import { setAuthToken } from '../services/api';
import SupabaseConfigError from '../components/SupabaseConfigError';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [configState, setConfigState] = useState('loading');

  useEffect(() => {
    let cancelled = false;

    async function resolveConfig() {
      const fromBuild =
        process.env.REACT_APP_SUPABASE_URL?.trim() && process.env.REACT_APP_SUPABASE_ANON_KEY?.trim();
      if (fromBuild) {
        setRuntimeSupabaseConfig(
          process.env.REACT_APP_SUPABASE_URL,
          process.env.REACT_APP_SUPABASE_ANON_KEY
        );
        if (!cancelled) setConfigState('ready');
        return;
      }

      try {
        const ok = await loadSupabaseConfigFromApi();
        if (cancelled) return;
        if (!ok || !isSupabaseConfigured()) {
          setConfigState('error');
          return;
        }
        setConfigState('ready');
      } catch {
        if (!cancelled) setConfigState('error');
      }
    }

    resolveConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  if (configState === 'loading') {
    return <div className="auth-boot">Loading…</div>;
  }

  if (configState === 'error') {
    return <SupabaseConfigError />;
  }

  return <AuthProviderInner>{children}</AuthProviderInner>;
}

function AuthProviderInner({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setAuthToken(s?.access_token ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setAuthToken(s?.access_token ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (employeeCode, password) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase is not configured');

    const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
    const res = await fetch(`${base}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employeeCode: employeeCode.trim(), password }),
    });

    let data = {};
    try {
      data = await res.json();
    } catch {
      throw new Error('Sign-in failed. Please try again.');
    }

    if (!res.ok) {
      throw new Error(data.error || 'Sign-in failed. Please try again.');
    }

    if (!data.access_token || !data.refresh_token) {
      throw new Error('Sign-in failed: missing session');
    }

    const { error } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    setAuthToken(null);
    const supabase = getSupabase();
    if (supabase) await supabase.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signIn,
      signOut,
    }),
    [session, loading, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
